/* eslint-disable @typescript-eslint/prefer-for-of */
// order-flow-order-summary.component.ts

import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { OrderDetailItem } from '../../../model/order-detail';
import { MeasurementContainer,FilledAccessory,SelectedAttribute } from '../../../model/attribute';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-order-flow-order-summary',
  templateUrl: './order-flow-order-summary.component.html',
  styleUrls: ['./order-flow-order-summary.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class OrderFlowOrderSummaryComponent implements OnChanges {

  @Input() orderDetail: OrderDetailItem[] = [];
  @Input() measurements: MeasurementContainer[] = [];
  @Input() accessories: FilledAccessory[] = [];
  @Input() comments: string = '';

  // Properties to store grouped data
  groupedOrderDetails: { groupName: string, items: OrderDetailItem[] }[] = [];
  groupedMeasurements: any[] = [];
  groupedAccessories: ProcessedAccessory[] = [];

  attachments: Array<{
    fileName: string;
    fileType: string;
    fileSize: number;
    base64Data: string;
  }> = [];

  tooManyFiles = false;
  ngOnChanges(changes: SimpleChanges): void {

    this.groupedOrderDetails = this.groupOrderDetails(this.orderDetail);
    this.groupedMeasurements = this.measurements;
    this.groupedAccessories = this.processAccessories(this.accessories);
    this.clearAttributeOptionsBrackets();

    //Add Both Comment Temporary
      const productDetailGroup = this.groupedOrderDetails.find(group => group.groupName.includes('Product Detail'));
        if (productDetailGroup) {
          const hasBothValue = productDetailGroup.items.some(item => item.value && item.value.toString().toLowerCase() === 'both');
          if (hasBothValue) {
            this.comments = 'For ankle pads inside + outside orders, please contact customer services.'
          }
        }
  }

  constructor(){

    // Filter Measurements
    this.groupedMeasurements = this.groupedMeasurements
      .map(container => {
        // Filter out measurements with empty 'value'
        const filteredMeasures = container.measures.filter((measure: { value: string | null; }) => 
          measure.value !== '' && measure.value != null &&  measure.value !== 'N/A'
        );

        return { ...container, measures: filteredMeasures };
      })
      // Optionally, remove containers with no measures left
      .filter(container => container.measures.length > 0);
      
      this.clearAttributeOptionsBrackets();
  }

  /**
   * Groups order details by group name.
   */
  private groupOrderDetails(orderDetail: OrderDetailItem[]): { groupName: string, items: OrderDetailItem[] }[] {
    // First, check if "Copy billing address to shipping address?" is "Yes"
    
    const copyField = orderDetail?.find(item => item.label === "Is the shipping address different from the billing address?");
    if (copyField && copyField.value.toLowerCase() === "no") {
        this.copyBillingToShipping(orderDetail);
    }

    // Now, proceed to group the order details
    const grouped: { [key: string]: OrderDetailItem[] } = {};
    orderDetail?.forEach(item => {
        const groupName = item.group || 'Sem Grupo'; // Default group name
        if (!grouped[groupName]) {
            grouped[groupName] = [];
        }
        grouped[groupName].push(item);
    });
    return Object.keys(grouped).map(groupName => ({ groupName, items: grouped[groupName] }));
}

  copyBillingToShipping(orderDetails: OrderDetailItem[]): void {
    const fieldMappings: { sourceLabel: string; targetLabel: string }[] = [
        { sourceLabel: "Payee Address 1", targetLabel: "Shipping Delivery Address 1" },
        { sourceLabel: "Payee Address 2", targetLabel: "Shipping Delivery Address 2" },
        { sourceLabel: "Payee Address 3", targetLabel: "Shipping Delivery Address 3" },
        { sourceLabel: "Payee City", targetLabel: "Shipping City" },
        { sourceLabel: "Payee State", targetLabel: "Shipping State" },
        { sourceLabel: "Payee Zip Code", targetLabel: "Shipping Zip Code" },
        { sourceLabel: "Payee Telephone Number", targetLabel: "Shipping  Telephone Number" },
    ];

    fieldMappings.forEach(mapping => {
        const sourceField = orderDetails.find(detail => detail.label === mapping.sourceLabel);
        const targetField = orderDetails.find(detail => detail.label === mapping.targetLabel);
        if (sourceField && targetField) {
            // Copy the value from source to target
            targetField.value = sourceField.value;
        } else {
            console.warn(`Mapping failed: Source (${mapping.sourceLabel}) or Target (${mapping.targetLabel}) field not found.`);
        }
    });

}


 /**
   * Processes accessories to extract Text componentType attributes.
   * @param accessories The list of accessories.
   * @returns A list of processed accessories with Text attributes.
   */
 private processAccessories(accessories: FilledAccessory[]): ProcessedAccessory[] {
  return accessories.map(accessory => {
    // Extract Text attributes with non-empty 'value's
    const textAttributes = accessory.accessory.attributes
      .filter(attr => attr.componentType === 'Text' && attr.value !== '' && attr.value != null)
      .map(attr => ({ name: attr.name, value: attr.value as string | number }));

    // Filter out selectedAttributes with empty 'value's
    const filteredSelectedAttributes = accessory.selectedAttributes?.filter(attr => 
      attr.value !== '' && attr.value != null
    );

    return {
      ...accessory,
      selectedAttributes: filteredSelectedAttributes,
      textAttributes: textAttributes,
    };
  })
  // Optionally, remove accessories with no selectedAttributes and no textAttributes
  .filter(accessory => accessory.selectedAttributes.length > 0 || accessory.textAttributes.length > 0);
}

  /**
   * Helper method to get the selected value label for non-text attributes.
   * @param attr The selected attribute.
   * @returns The label of the selected value or the raw value if not found.
   */
  getSelectedValue(attr: SelectedAttribute): string {
    if (attr.componentType === 'Select' || attr.componentType === 'RadioButton') {
      const selectedOption = attr.values.find((val: any) => val.value === attr.value);
      return selectedOption ? selectedOption.value : String(attr.value);
    }
    return String(attr.value);
  }

   /**
   * Handle when files are selected via the file input.
   */
  onFileChange(event: Event): void {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    this.processFiles(files);
    target.value = '';
  }
}

  /**
   * Handle when files are dropped into the drop zone.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const dropZone = event.currentTarget as HTMLElement;
    dropZone.classList.remove('dragover');

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone.classList.add('dragover');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone.classList.remove('dragover');
  }

  private processFiles(files: FileList): void {
    const maxFileSize = 10 * 1024 * 1024; // 10 MB
    const maxFilesAllowed = 5;
    
    const currentCount = this.attachments.length;
    const availableSlots = maxFilesAllowed - currentCount;
    
    if (availableSlots <= 0) {
      this.tooManyFiles = true;
      return;
    }

    const newFiles = Array.from(files);
    if (newFiles.length > availableSlots) {
      this.tooManyFiles = true;
    }
    
    const filesToProcess = newFiles.slice(0, availableSlots);

    const promises: Promise<void>[] = [];

    for (const file of filesToProcess) {
      if (!file.type.startsWith('image/')) {
        alert(`File ${file.name} is not an image and will be skipped.`);
        continue;
      }

      if (file.size > maxFileSize) {
        alert(`File ${file.name} exceeds the 5MB size limit and will be skipped.`);
        continue;
      }

      const reader = new FileReader();

      const filePromise = new Promise<void>((resolve, reject) => {
        reader.onload = (e: any) => {
          const base64Result = e.target.result;
          const base64Data = base64Result.split(',')[1];

          this.attachments.push({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            base64Data: base64Data,
          });
          resolve();
        };

        reader.onerror = (error) => reject(error);
      });

      promises.push(filePromise);
      reader.readAsDataURL(file);
    }

    Promise.all(promises)
      .then(() => {
      })
      .catch((error) => {
        console.error('Error reading files:', error);
      });
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
    // Reset the too many files flag when removing attachments
    if (this.attachments.length < 5) {
      this.tooManyFiles = false;
    }
  }

clearAttributeOptionsBrackets(): void {
    if (environment && environment.production) {
      if (this.groupedMeasurements.length > 0) {
        this.groupedMeasurements.forEach((attribute: any) => {
          if (attribute.attributeOptionName && attribute.attributeOptionName.includes('(')) {
            const match = attribute.attributeOptionName.match(/\((\d+)\)/);
            if (!match) {
              attribute.attributeOptionName = attribute.attributeOptionName.split('(')[0].trim();
            }
          }
        });
      }

      if (this.groupedAccessories.length > 0) {
        this.groupedAccessories.forEach((option: any) => {
          if (option.name && option.name.includes('(')) {
            const match = option.name.match(/\((\d+)\)/);
            if (!match) {
              option.name = option.name.split('(')[0].trim();
            }
          }
        });
      }

      if (this.groupedOrderDetails.length > 0) {
        this.groupedOrderDetails.forEach((group) => {
          group.items.forEach((item) => {
            if (item.label && item.label.includes('(')) {
              const match = item.label.match(/\((\d+)\)/);
              if (!match) {
                item.label = item.label.split('(')[0].trim();
              }
            }
          });
        });
      }
    }
  }
}

interface ProcessedAccessory extends FilledAccessory {
  textAttributes: { name: string; value: string | number }[];
}